/**
 * The nav sections of an app, their permission filter, the admin role gate and the initials of a
 * name (kernel apps, WS-13 section 3.2). Moved from the shell's `logic/nav-utils.ts` and
 * `logic/admin-utils.ts`. Every function is pure. One delta applies (R-119): the shell's filter
 * keeps an item whose `permission` is falsy (`null`, `0`, `false`, `''`, `NaN`), and this filter
 * keeps only an item whose `permission` is absent.
 */
import type { ShellPermissions } from '@zion/sdk-contracts';

/** One navigation section, as an app manifest declares it. */
export interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

/**
 * One item of a navigation section. `path` is relative to the app's mount, and the sidebar joins
 * it to the mount. `permission` names one of the nine zero-argument checkers of
 * {@link ShellPermissions}: `isOrgAdmin`, `isOrgOwner`, `canManageReleases`, `canManageChannels`,
 * `canManageApps`, `canViewAuditTrail`, `canManageMembers`, `canManageContent` and
 * `canManageOrgSettings`.
 */
export interface NavItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  badge?: number;
  permission?: string;
}

/** The zero-argument checkers of {@link ShellPermissions}, the only names a nav item may use. */
type NavChecker =
  | 'isOrgAdmin'
  | 'isOrgOwner'
  | 'canManageReleases'
  | 'canManageChannels'
  | 'canManageApps'
  | 'canViewAuditTrail'
  | 'canManageMembers'
  | 'canManageContent'
  | 'canManageOrgSettings';

const NAV_CHECKERS: ReadonlySet<string> = new Set<NavChecker>([
  'isOrgAdmin',
  'isOrgOwner',
  'canManageReleases',
  'canManageChannels',
  'canManageApps',
  'canViewAuditTrail',
  'canManageMembers',
  'canManageContent',
  'canManageOrgSettings',
]);

function isNavChecker(name: string): name is NavChecker {
  return NAV_CHECKERS.has(name);
}

/**
 * Filter navigation sections by permission.
 *
 * Caller: the sidebar (WS-13b), with `buildShellPermissions(viewer.orgRoles, viewer.orgPermissions)`.
 *
 * - An item whose `permission` key is absent or `undefined` stays.
 * - An item whose `permission` names one of the nine checkers of {@link NavItem} stays when that
 *   checker answers `true`.
 * - Any other item goes, for every viewer, and the filter does not throw. This covers a `false`
 *   answer, a member that is not a function (`orgPermissions`), and the one-argument predicates
 *   `hasPermission` and `hasOrgPermission`. It also covers an `Object.prototype` name such as
 *   `toString` or `hasOwnProperty`, a name that `ShellPermissions` does not have, and every wire
 *   value that is not a non-empty string: `null`, `0`, `false`, `''`, `NaN`, another number and an
 *   object (R-119). `buildSuiteGroups` refuses the same values.
 * - A section that has no item left goes.
 *
 * @returns New section objects; the input stays as it was. The filter shapes the view only. The
 *   kernel guard of WS-19 is the authority for a privileged operation.
 */
export function filterNavSections(sections: readonly NavSection[], permissions: ShellPermissions): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (item.permission === undefined) return true;
        if (typeof item.permission !== 'string' || !isNavChecker(item.permission)) return false;
        return permissions[item.permission]() === true;
      }),
    }))
    .filter((section) => section.items.length > 0);
}

const ADMIN_ROLES: readonly string[] = ['admin', 'owner'];

/**
 * True when the viewer's org roles hold `admin` or `owner`.
 *
 * Caller: the header, which shows the Administration tile only when this answers `true` and the
 * registry holds the admin app's row. The gate shapes the view only; the kernel guard of WS-19 is
 * the authority.
 */
export function canAccessAdmin(orgRoles: readonly string[]): boolean {
  return orgRoles.some((role) => ADMIN_ROLES.includes(role));
}

/**
 * The first character of `label`, after leading white space, in upper case. `?` when the label is
 * empty or holds white space only. The header uses it as the mark of a chooser tile that has no
 * `chooser.icon`.
 */
export function firstInitial(label: string): string {
  const ch = label.trim().charAt(0);
  return ch === '' ? '?' : ch.toUpperCase();
}

/**
 * The initials of a full name: the first character of each word that a single space separates,
 * at most two, in upper case. `?` for `null` or `undefined`, and `''` for an empty name.
 */
export function initials(name: string | undefined | null): string {
  if (name == null) return '?';
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
